import { useContext, useEffect } from 'react'
import { Link, withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import {
    CheckboxTableRow,
    ExTable,
    ModalButton,
    SelectionFixedFooter,
    TableBody,
    TableCell,
    TableHead,
    TableWrapper,
    useMultipleCheckForm,
} from 'components/shared/widget'
import { CreateTaskGroupForm, useCancelTaskSetApi, useCompleteTaskSetApi, useCreateTaskGroupWithTaskSetsApi, useDeleteTaskSetApi, useFetchTaskSetsApi } from 'api/task_set'
import { MaterialTaskSetData, TaskSet, TaskSetSearchForm } from 'entities/task_set'
import { Pagination } from 'components/shared/pagination'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { SearchConditionView } from 'components/shared/search_drawer'
import { GlobalStateContext } from 'contexts/global_state_context'
import { taskSetColumnInfoList, TaskSetTableData } from './task_set_table'
import { DisplaySetting, DisplaySettingModal, TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import qs from 'qs'
import { useState } from 'react'
import { EditTaskSetForm } from './task_set_form'
import { Flex } from 'components/shared/flex'
import { Button, Modal, Table } from 'react-bootstrap'
import moment from 'moment'
import { toDateTimeText } from 'utils/util'
import { CheckboxField } from 'components/shared/input'
import { TaskSetViewModel } from 'view_models/task_set_view_model'
import { taskMasterSetKindJa } from 'entities/task_master_set'
import { useCancelTaskApi, useCompleteTaskApi } from 'api/task'

type Props = {
    history: H.History
}

const TaskSetsPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const query = useQuery<TaskSetSearchForm>()
    const searchForm = useForm<TaskSetSearchForm>({ hideOtherUserGroup: true, ...query, userGroupId: globalState.user.userGroups?.map((ug) => ug.id).join(',') })
    const api = useFetchTaskSetsApi(searchForm)
    const deleteApi = useDeleteTaskSetApi()
    const multipleCheckForm = useMultipleCheckForm<TaskSet>()
    const taskSetDisplaySetting = useDisplaySetting(taskSetColumnInfoList(searchForm), 'TaskSet')
    const displaySettings: DisplaySetting[] = [taskSetDisplaySetting]
    const [selectedTaskSet, setSelectedTaskSet] = useState<TaskSet>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const [showTasks, setShowTasks] = useState<boolean>(false) 
    const [taskSets, setTaskSets] = useState<TaskSet[]>([])
    const completeTaskSetApi = useCompleteTaskSetApi()
    const cancelTaskSetApi = useCancelTaskSetApi()
    const completeTaskApi = useCompleteTaskApi()
    const cancelTaskApi = useCancelTaskApi()
    const createTaskGroupForm = useForm<CreateTaskGroupForm>({ kind: query.kind })
    const createTaskGroupApi = useCreateTaskGroupWithTaskSetsApi()

    useEffect(() => {
        api.execute()
    }, [])

    useEffect(() => {
        if (api.isSuccess()) {
            setTaskSets(api.response.results)
        }
    }, [api.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(deleteApi.loading)
        if (deleteApi.isSuccess()) {
            api.execute()
        }
    }, [deleteApi.loading])

    const handleClickRemoveFilter = (key: keyof TaskSetSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffect(() => {
        if (searchForm.object.hideOtherUserGroup) {
            searchForm.updateObject('userGroupId', globalState.user.userGroups?.map((ug) => ug.id).join(','))
        }
        else {
            searchForm.updateObject('userGroupId', '')
        }
    }, [searchForm.object.hideOtherUserGroup])

    useEffect(() => {
        const queryParams = qs.stringify({ ...searchForm.object, page: api.pageSet.page, perPage: api.pageSet.perPage })
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`task_sets?${queryParams}`)
        }
    }, [searchForm.object])

    useEffectSkipFirst(() => {
        globalState.setLoading(completeTaskApi.loading)
    }, [completeTaskApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(completeTaskSetApi.loading)
    }, [completeTaskSetApi.loading])

    useEffectSkipFirst(() => {
        createTaskGroupForm.update((f) => (f.taskSetIds = multipleCheckForm.ids() as number[]))
    }, [multipleCheckForm.form.object.items])

    useEffectSkipFirst(() => {
        globalState.setLoading(createTaskGroupApi.loading)
        if (createTaskGroupApi.isSuccess()) {
            props.history.push(`/tasks/materials?kind=${createTaskGroupApi.response.taskGroup?.kind}&taskGroupId=${createTaskGroupApi.response.taskGroup?.id}`)
        }
    }, [createTaskGroupApi.loading])

    return (
        <>
            <EditTaskSetForm
                taskSet={selectedTaskSet}
                show={showEdit}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    api.execute()
                    setShowEdit(false)
                }}
            />

            <Modal
                show={showTasks}
                size="lg"
                onHide={() => {
                    setShowTasks(false)
                }}
            >
                <Modal.Header closeButton>
                    <>タスク一覧</>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <Table>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>タスク</th>
                                    <th style={{ width: 160 }}>完了日時</th>
                                    <th style={{ width: 160 }}>担当者</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedTaskSet.tasks?.map((task, tIdx) => {
                                    return (
                                        <tr
                                            key={'task-' + task.id}
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => {
                                                if (query.kind === 'MATERIAL') {
                                                    return
                                                }
                                                if (task.completedAt) {
                                                    globalState.showConfirm('取り消しますか？', () => {
                                                        task.completedAt = undefined
                                                        task.completeUser = undefined
                                                        selectedTaskSet.completedAt = undefined
                                                        cancelTaskApi.execute(task.id!)
                                                    })
                                                } else {
                                                    task.completedAt = moment().format('YYYY-MM-DD HH:mm:ss')
                                                    task.completeUser = globalState.user
                                                    completeTaskApi.execute(task.id!)
                                                    if (selectedTaskSet.tasks?.every((t) => !!t.completedAt)) {
                                                        selectedTaskSet.completedAt = moment().format('YYYY-MM-DD HH:mm:ss')
                                                    }
                                                }
                                                setTaskSets([...taskSets])
                                                // const copyTasks = [...taskSet.tasks!]
                                                // copyTasks[tIdx] = { ...task, completedAt: moment().format('YYYY-MM-DD') }
                                                // const copyTaskSets = [...taskSets]
                                                // copyTaskSets[index] = { ...taskSet, tasks: copyTasks }
                                                // setTaskSets(copyTaskSets)
                                            }}
                                        >
                                            <td style={{ width: 50 }}>
                                                <input type="checkbox" checked={!!task.completedAt} readOnly={true} />
                                            </td>
                                            <td style={{ textDecoration: task.completedAt ? 'line-through' : 'none' }}>{task.title}</td>
                                            <td>{toDateTimeText(task.completedAt)}</td>
                                            <td>{task.completeUser?.fullName}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer></Modal.Footer>
            </Modal>
            <ContentHeader
                headerTitle={`やることリスト${query.kind ? `(${taskMasterSetKindJa[query.kind]})` : ''}`}
                left={
                    <>
                        {/* <CopyTableButton /> */}

                        {/* <NewTaskSetForm
                            onComplete={() => {
                                api.execute()
                            }}
                        /> */}
                        <SearchConditionView
                            searchForm={searchForm}
                            onClickRemoveButton={handleClickRemoveFilter}
                            hiddenSearchParams={['incomplete', 'kind', 'userGroupId', 'hideOtherUserGroup']}
                        />
                    </>
                }
                right={
                    <Flex alignItems="center">
                        <CheckboxField form={searchForm} attr="hideOtherUserGroup" removeFalse checkboxLabel="所属タグのみ表示" />
                        <CheckboxField form={searchForm} attr="incomplete" removeFalse checkboxLabel="未完了のみ表示" />
                        <DisplaySettingModal displaySettings={displaySettings} />
                    </Flex>
                }
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <div>{query.kind === 'MATERIAL' && <Link to={'/task_groups?kind=MATERIAL'}>資材タスク作業履歴</Link>}</div>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={multipleCheckForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <CheckboxTableRow head allCheck allItems={api.response.results} multipleCheckForm={multipleCheckForm} showEdit>
                                {/* <ActionTableCell th width={60} /> */}
                                <TableHeads displaySetting={taskSetDisplaySetting} />

                                <TableCell th width={120} />
                                {query.kind === 'MATERIAL' ? (
                                    <></>
                                ) : (
                                    <>
                                        <TableCell th width={70} />
                                    </>
                                )}
                                <TableCell th width={70} />
                            </CheckboxTableRow>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={taskSetDisplaySetting.getColumnLength() + (query.kind === 'MATERIAL' ? 1 : 3)}>
                            {taskSets.map((taskSet: TaskSet, index: number) => {
                                return (
                                    <CheckboxTableRow key={`taskSet-${taskSet.id}`} item={taskSet} multipleCheckForm={multipleCheckForm}>
                                        {/* <ActionTableCell
                                            action="edit"
                                            onClick={() => {
                                                setSelectedTaskSet(taskSet)
                                                setShowEdit(true)
                                            }}
                                        /> */}
                                        <TaskSetTableData
                                            key={`task-set-${taskSet.id}-${taskSet.tasks?.filter((t) => !!t.completedAt).length}`}
                                            taskSet={taskSet}
                                            displaySetting={taskSetDisplaySetting}
                                        />
                                        <>
                                            <TableCell align="center">
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        setShowTasks(true)
                                                        setSelectedTaskSet(taskSet)

                                                        e.stopPropagation()
                                                        e.preventDefault()
                                                    }}
                                                >
                                                    タスク詳細
                                                </Button>
                                            </TableCell>
                                            {query.kind !== 'MATERIAL' &&
                                                (() => {
                                                    const vm = new TaskSetViewModel(taskSet)
                                                    return (
                                                        <TableCell align="center">
                                                            {!vm.isCompleted() ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="success"
                                                                    onClick={(e) => {
                                                                        taskSet.tasks?.map((task) => {
                                                                            task.completedAt = moment().format('YYYY-MM-DD HH:mm:ss')
                                                                            task.completeUser = globalState.user
                                                                        })
                                                                        completeTaskSetApi.execute(taskSet.id!)
                                                                        setTaskSets(taskSets)
                                                                        e.stopPropagation()
                                                                        e.preventDefault()
                                                                    }}
                                                                >
                                                                    完了
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    variant="danger"
                                                                    onClick={(e) => {
                                                                        globalState.showConfirm('取り消しますか？', () => {
                                                                            taskSet.tasks?.map((task) => {
                                                                                task.completedAt = undefined
                                                                                task.completeUser = undefined
                                                                            })
                                                                            cancelTaskSetApi.execute(taskSet.id!)
                                                                            setTaskSets(taskSets)
                                                                        })

                                                                        e.stopPropagation()
                                                                        e.preventDefault()
                                                                    }}
                                                                >
                                                                    取消
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    )
                                                })()
                                            }
                                            <TableCell align="center">
                                                <ModalButton
                                                    openButton={
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                        >
                                                            削除
                                                        </Button>
                                                    }
                                                    handleOpen={() => setSelectedTaskSet(taskSet)}
                                                    modalBody={
                                                        <>
                                                            「{selectedTaskSet.title} {`${(new TaskSetViewModel(selectedTaskSet).data() as MaterialTaskSetData)?.institution_name ?? '--'}`}」を削除します。
                                                            <div>本当によろしいですか？</div>
                                                        </>
                                                    }
                                                    submitButton={
                                                        <Button
                                                            variant="danger"
                                                        >
                                                            削除
                                                        </Button>
                                                    }
                                                    handleClick={() => {
                                                        deleteApi.execute(selectedTaskSet.id!)
                                                    }}

                                                />
                                            </TableCell>
                                        </>
                                    </CheckboxTableRow>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>

            <SelectionFixedFooter
                ids={multipleCheckForm.ids()}
                onClickReset={() => multipleCheckForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="id"
                tableName="TaskSet"
            >
                <div>
                    {query.kind === 'MATERIAL' && (
                        <ModalButton
                            openButton={<Button>選択したタスクの作業を開始する</Button>}
                            modalHeader={<>確認</>}
                            modalBody={
                                <div>
                                    <div>選択したタスクの作業を開始しますか？</div>
                                </div>
                            }
                            submitButton={
                                <Button
                                    variant="success"
                                    onClick={() => {
                                        createTaskGroupApi.execute(createTaskGroupForm)
                                    }}
                                >
                                    開始
                                </Button>
                            }
                        />
                    )}
                </div>
            </SelectionFixedFooter>
        </>
    )
}

export default withRouter(TaskSetsPage)
